import React, { useEffect, useMemo } from "react";
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { useAtomValue } from "jotai";
import { Card, CardContent, Typography } from "@mui/material";

import { eachConditionDataByKeyAtom, EachConditionDataKeys } from "atoms/Rtool/mtgAtoms";
import { toChartData } from "utils/charts/toChartData";
import { useRechartsLegendSync } from "utils/charts/useRechartsLegendSync";
import { HoverLegend } from "utils/charts/HoverLegend";
import { lockReferenceLine } from "utils/charts/lockReferenceLine";
import { buildIndexMap, makeTicksFromData } from "utils/charts/AxisLabel";
import { HEV_SERIES_3MTG, SERIES_9, SERIES_12 } from "./seriesDefinitions";
import { useSeriesSelection } from "utils/SeriesSelectionProvider/SeriesSelectionContext";
import { phenomenonIdAtom } from "atoms/Rtool/stageConfigAtoms";
import { powertrainAtom } from "atoms/powertrainAtom";

interface Props {
    title: string;
    graph_id: EachConditionDataKeys;
}

const EXCLUDE = new Set(["rpm", "vel", "index"]);

export const DisplayContribution: React.FC<Props> = ({ title, graph_id }) => {
    const memoAtom = useMemo(() => eachConditionDataByKeyAtom(graph_id), [graph_id]);
    const target = useAtomValue(memoAtom);

    const ph = useAtomValue(phenomenonIdAtom);
    const phenomenonGroupId = ph ? ph.split("_")[1] : "";
    const indexScale = phenomenonGroupId === "aco" ? 10 : 1;
    const isHev = useAtomValue(powertrainAtom) === 'hev';

    const seriesSelection = useSeriesSelection();
    const selectedSeriesKeys = seriesSelection?.selectedSeriesKeys;

    const chartData = useMemo(() => {
        if (!target || target.status !== "success") return [];
        return toChartData(target, indexScale);
    }, [target, indexScale]);

    const firstRow = chartData[0] as Record<string, unknown> | undefined;
    const dataKeysCount = firstRow
        ? Object.keys(firstRow).filter((k) => !EXCLUDE.has(k)).length
        : 0;

    const baseSeriesList = useMemo(
        () => isHev && dataKeysCount === 9 ? HEV_SERIES_3MTG : (dataKeysCount === 9 ? SERIES_9 : SERIES_12),
        [dataKeysCount, isHev]
    );

    const hasData = chartData.length > 0;

    useEffect(() => {
        if (seriesSelection && hasData) {
            seriesSelection.registerAvailableSeries(baseSeriesList);
        }
    }, [baseSeriesList, hasData, seriesSelection]);

    const seriesList = useMemo(() => {
        if (!selectedSeriesKeys) return baseSeriesList;
        if (selectedSeriesKeys.length === 0) return [];
        const allowed = new Set(selectedSeriesKeys);
        return baseSeriesList.filter((series) => allowed.has(series.key));
    }, [baseSeriesList, selectedSeriesKeys]);

    // 共通フック：同期イベントで外部凡例を更新
    const { setIndexRef, HiddenSyncTooltip, handlers, lockedValue } = useRechartsLegendSync(chartData, {
        xKey: "index",
        resetOnData: true,
    });

    if (!target) return <Typography variant="body2" align="center">データなし</Typography>;
    if (target.status === "notstarted" || target.status === "loading")
        return <Typography variant="body2" align="center">読み込み中...</Typography>;
    if (target.status === "error")
        return <Typography variant="body2" align="center" color="error">データ読み込み中にエラーが発生しました。</Typography>;
    if (!chartData.length) return <Typography variant="body2" align="center">データなし</Typography>;

    const ticks = makeTicksFromData(chartData, "index", 250 * indexScale);
    const rpmByIndex = buildIndexMap(chartData, "index", "rpm");
    const velByIndex = buildIndexMap(chartData, "index", "vel");

    return (
        <Card key={title} variant="outlined">
            <CardContent>
                <Typography variant="subtitle1" gutterBottom>{title}</Typography>

                {/* 値はここで表示（Tooltipは使わない） */}
                <HoverLegend seriesList={seriesList} data={chartData as any[]} setIndexRef={setIndexRef} />

                <ResponsiveContainer width="100%" height={240}>
                    <AreaChart
                        syncId="rTooltip"
                        syncMethod="value"
                        data={chartData}
                        onMouseMove={handlers.onMouseMove}
                        onMouseLeave={handlers.onMouseLeave}
                        onClick={handlers.onClick}
                    >
                        <XAxis
                            dataKey="index"
                            xAxisId="0"
                            height={18}
                            ticks={ticks}
                            tick={{ fontSize: 8 }}
                            label={{ value: "Frequency", position: "insideLeft", offset: -20, fontSize: 8 }}
                        />
                        <XAxis
                            dataKey="index"
                            xAxisId="1"
                            height={18}
                            ticks={ticks}
                            tick={{ fontSize: 8 }}
                            tickFormatter={(v: number) =>
                                rpmByIndex.has(v) ? Math.round(rpmByIndex.get(v)!).toString() : ""
                            }
                            label={{ value: "RPM", position: "insideLeft", offset: -20, fontSize: 8 }}
                        />
                        <XAxis
                            dataKey="index"
                            xAxisId="2"
                            height={18}
                            ticks={ticks}
                            tick={{ fontSize: 8 }}
                            tickFormatter={(v: number) =>
                                velByIndex.has(v) ? Math.round(velByIndex.get(v)!).toString() : ""
                            }
                            label={{ value: "Velocity", position: "insideLeft", offset: -20, fontSize: 8 }}
                        />
                        <YAxis
                            domain={[0, 105]}
                            ticks={[0, 20, 40, 60, 80, 100]}
                            tick={{ fontSize: 10 }}
                        />

                        {/* 同期イベント受け取り用（非表示） */}
                        {HiddenSyncTooltip}

                        {seriesList.map((s) => (
                            <Area
                                key={s.key}
                                type="monotone"
                                dataKey={s.key}
                                name={s.name}
                                stackId="1"
                                stroke={s.color}
                                fill={s.color}
                                fillOpacity={1.0}
                                dot={false}
                                isAnimationActive={false}
                            />
                        ))}
                        {lockReferenceLine(lockedValue)}
                    </AreaChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
};
